import React, { useEffect, useState, useLayoutEffect, useCallback } from "react";
import { X, ArrowLeft, ArrowRight, Compass, Sparkles, BookOpen, Award, Umbrella, Home } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useDemoTour } from "@/hooks/useDemoTour";
import { DEMO_TOUR_STEPS } from "@/config/demoTourSteps";

const PAD = 8;              // extra pixels around the highlighted target
const CALLOUT_W = 320;      // tooltip width — used for placement math
const CALLOUT_MARGIN = 16;  // gap between spotlight edge and tooltip
const POLL_MS = 100;        // interval when waiting for the target to mount
const POLL_TIMEOUT_MS = 3000;

const TAB_ICONS = {
  explore: Compass, dream: Sparkles, chapters: BookOpen,
  passport: Award, lounge: Umbrella, home: Home,
};

/**
 * Guided product-tour overlay. Renders nothing when `active === false`
 * in the tour context. When active, waits for the current step's
 * target selector to mount in the DOM, then paints a dark backdrop
 * with a cutout around it and a floating callout with copy + controls.
 *
 * Key behaviors:
 *   • Auto-navigates via react-router when the current step's `path`
 *     differs from the current URL.
 *   • Skips optional steps whose targets never mount (graceful).
 *   • Fires re-layout on scroll / resize (spotlight tracks the target).
 *   • Keyboard: ← / → advance, Esc exits.
 *   • z-index 9999 so it renders above modals.
 */
export default function DemoTour() {
  const { active, stepIndex, next, prev, jump, close } = useDemoTour();
  const loc = useLocation();
  const [rect, setRect] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | waiting | ready | missing
  // Guards against the "seizure": tracks the last step we scrolled for
  // (so smooth-scroll fires ONCE per step, not on every re-measure) and
  // the current rAF handle for throttled relayouts on scroll/resize.
  const scrolledForStepRef = React.useRef(-1);
  const relayoutRAFRef = React.useRef(0);

  const total = DEMO_TOUR_STEPS.length;
  const step = active ? DEMO_TOUR_STEPS[stepIndex] : null;

  // Locate the target element and measure it. Polls up to POLL_TIMEOUT_MS
  // to give the page time to render after a route change.
  useEffect(() => {
    if (!active || !step) return;
    // If the URL hasn't caught up yet, do nothing (the router will
    // re-trigger this effect once loc.pathname matches).
    if (loc.pathname !== step.path) {
      setStatus("waiting");
      setRect(null);
      return;
    }
    let cancelled = false;
    setStatus("waiting");
    const startedAt = Date.now();
    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(step.selector);
      if (el) {
        // Only scroll ONCE per step. Otherwise every relayout would
        // re-trigger a smooth-scroll → cascading scroll events → setRect
        // firing at 60fps → the "seizure" flicker.
        if (scrolledForStepRef.current !== stepIndex) {
          scrolledForStepRef.current = stepIndex;
          // Instant (no smooth) — smooth scroll fires ~30 scroll events
          // over ~500ms which was the root cause of the flicker.
          try { el.scrollIntoView({ behavior: "auto", block: "center" }); }
          catch { /* Safari older API */ el.scrollIntoView(); }
        }
        // Measure on the next frame so the (instant) scroll has settled.
        requestAnimationFrame(() => {
          if (cancelled) return;
          const r = el.getBoundingClientRect();
          setRect((prev) => {
            // Skip the state update when the rect hasn't meaningfully
            // changed (prevents render thrash from sub-pixel jitter).
            if (
              prev &&
              Math.abs(prev.left - r.left) < 1 &&
              Math.abs(prev.top - r.top) < 1 &&
              Math.abs(prev.width - r.width) < 1 &&
              Math.abs(prev.height - r.height) < 1
            ) return prev;
            return r;
          });
          setStatus("ready");
        });
        return;
      }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        setStatus("missing");
        setRect(null);
        return;
      }
      setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { cancelled = true; };
  }, [active, stepIndex, step, loc.pathname]);

  // When a step's target is missing, auto-advance so the tour keeps flowing.
  useEffect(() => {
    if (status !== "missing") return;
    const t = setTimeout(() => {
      if (stepIndex + 1 < total) next(total);
      else close("/home");
    }, 300);
    return () => clearTimeout(t);
  }, [status, stepIndex, total, next, close]);

  // Auto-navigate to the current step's path if we're not already there.
  useEffect(() => {
    if (!active || !step) return;
    if (loc.pathname === step.path) return;
    // The tour hook was created inside <BrowserRouter>, so `next`/`prev`
    // don't own navigation — we push the URL directly. Using
    // window.history keeps the tour engine independent of a nav ref.
    window.history.pushState({}, "", step.path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [active, step, loc.pathname]);

  // Keep the spotlight tracking the target on scroll / resize. Throttled
  // to a single requestAnimationFrame per burst so a scroll event storm
  // (which is what caused the original flicker) cannot cascade re-renders.
  useLayoutEffect(() => {
    if (status !== "ready" || !step) return;
    const relayout = () => {
      if (relayoutRAFRef.current) return;
      relayoutRAFRef.current = requestAnimationFrame(() => {
        relayoutRAFRef.current = 0;
        const el = document.querySelector(step.selector);
        if (!el) return;
        const r = el.getBoundingClientRect();
        setRect((prev) => {
          if (
            prev &&
            Math.abs(prev.left - r.left) < 1 &&
            Math.abs(prev.top - r.top) < 1 &&
            Math.abs(prev.width - r.width) < 1 &&
            Math.abs(prev.height - r.height) < 1
          ) return prev;
          return r;
        });
      });
    };
    window.addEventListener("scroll", relayout, true);
    window.addEventListener("resize", relayout);
    return () => {
      window.removeEventListener("scroll", relayout, true);
      window.removeEventListener("resize", relayout);
      if (relayoutRAFRef.current) {
        cancelAnimationFrame(relayoutRAFRef.current);
        relayoutRAFRef.current = 0;
      }
    };
  }, [status, step]);

  // Keyboard controls.
  const handleKey = useCallback((e) => {
    if (!active) return;
    if (e.key === "Escape") { e.preventDefault(); close("/home"); }
    else if (e.key === "ArrowRight") {
      e.preventDefault();
      if (stepIndex + 1 >= total) close("/home");
      else next(total);
    }
    else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
  }, [active, stepIndex, total, next, prev, close]);

  useEffect(() => {
    if (!active) return;
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [active, handleKey]);

  if (!active || !step) return null;

  // ── Compute callout position ─────────────────────────────────────
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const hasRect = status === "ready" && rect;

  let calloutTop = vh / 2 - 100;
  let calloutLeft = vw / 2 - CALLOUT_W / 2;
  let cutoutStyle = null;

  if (hasRect) {
    // Cutout box (padded)
    const bx = Math.max(0, rect.left - PAD);
    const by = Math.max(0, rect.top - PAD);
    const bw = rect.width + PAD * 2;
    const bh = rect.height + PAD * 2;

    cutoutStyle = { left: bx, top: by, width: bw, height: bh };

    // Placement — prefer below if there's room, else above; then squeeze
    // sideways if too narrow near the edges.
    const roomBelow = vh - (by + bh);
    const roomAbove = by;
    if (step.placement === "left" && bx > CALLOUT_W + CALLOUT_MARGIN) {
      calloutTop = Math.max(20, by);
      calloutLeft = bx - CALLOUT_W - CALLOUT_MARGIN;
    } else if (step.placement === "right" && vw - (bx + bw) > CALLOUT_W + CALLOUT_MARGIN) {
      calloutTop = Math.max(20, by);
      calloutLeft = bx + bw + CALLOUT_MARGIN;
    } else if (step.placement === "top" || roomBelow < 220) {
      calloutTop = Math.max(20, by - 220 - CALLOUT_MARGIN);
      calloutLeft = Math.min(vw - CALLOUT_W - 20, Math.max(20, bx + bw / 2 - CALLOUT_W / 2));
      if (roomAbove < 200) {
        // Neither above nor below has room — pin to bottom of viewport.
        calloutTop = vh - 240;
      }
    } else {
      calloutTop = by + bh + CALLOUT_MARGIN;
      calloutLeft = Math.min(vw - CALLOUT_W - 20, Math.max(20, bx + bw / 2 - CALLOUT_W / 2));
    }
  }

  const isFirst = stepIndex === 0;
  const isLast = stepIndex >= total - 1;
  const TabIcon = TAB_ICONS[step.tab] || Compass;

  return (
    <div
      data-testid="demo-tour-overlay"
      className="fixed inset-0"
      style={{ zIndex: 9999 }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── Backdrop with cutout ─────────────────────────────────── */}
      {hasRect ? (
        <>
          {/* Four dark panels forming a border around the cutout. */}
          <div className="absolute bg-black/70 transition-all duration-300" style={{
            top: 0, left: 0, right: 0, height: cutoutStyle.top,
          }} />
          <div className="absolute bg-black/70 transition-all duration-300" style={{
            top: cutoutStyle.top + cutoutStyle.height, left: 0, right: 0, bottom: 0,
          }} />
          <div className="absolute bg-black/70 transition-all duration-300" style={{
            top: cutoutStyle.top, left: 0, width: cutoutStyle.left, height: cutoutStyle.height,
          }} />
          <div className="absolute bg-black/70 transition-all duration-300" style={{
            top: cutoutStyle.top,
            left: cutoutStyle.left + cutoutStyle.width,
            right: 0, height: cutoutStyle.height,
          }} />
          {/* Cutout border — gold ring around the spotlight */}
          <div className="absolute rounded-xl ring-2 ring-tb-gold ring-offset-2 ring-offset-transparent pointer-events-none transition-all duration-300"
               style={cutoutStyle} />
        </>
      ) : (
        <div className="absolute inset-0 bg-black/70" />
      )}

      {/* ── Persistent top control bar ───────────────────────────── */}
      <div className="absolute top-4 left-0 right-0 flex items-center justify-between px-4 pointer-events-none">
        <button
          type="button"
          data-testid="demo-tour-close"
          onClick={() => close("/home")}
          aria-label="Exit tour"
          className="pointer-events-auto w-10 h-10 rounded-full bg-white/95 text-tb-navy shadow-lg hover:bg-white flex items-center justify-center tb-tap"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="pointer-events-auto flex items-center gap-2 bg-white/95 rounded-full px-3 py-1.5 shadow-lg">
          <TabIcon className="w-4 h-4 text-tb-navy" />
          <span className="font-hand text-tb-navy/80 text-xs uppercase tracking-wider">
            {step.tab}
          </span>
          <span className="font-hand text-tb-navy/50 text-xs">
            · step {stepIndex + 1} of {total}
          </span>
        </div>
        <div className="pointer-events-auto flex items-center gap-2">
          <button
            type="button"
            data-testid="demo-tour-prev"
            onClick={prev}
            disabled={isFirst}
            aria-label="Previous"
            className="w-10 h-10 rounded-full bg-white/95 text-tb-navy shadow-lg hover:bg-white flex items-center justify-center disabled:opacity-40 tb-tap"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            data-testid="demo-tour-next"
            onClick={() => (isLast ? close("/home") : next(total))}
            aria-label={isLast ? "Finish" : "Next"}
            className="h-10 px-4 rounded-full bg-tb-navy text-tb-gold-bright border-2 border-tb-gold shadow-lg hover:bg-tb-navy/90 flex items-center gap-2 font-hand text-sm tb-tap"
          >
            {isLast ? "finish" : "next"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Callout tooltip ──────────────────────────────────────── */}
      {status === "ready" && (
        <div
          data-testid="demo-tour-callout"
          className="absolute rounded-2xl bg-tb-cream border-2 border-tb-gold shadow-2xl p-5"
          style={{ top: calloutTop, left: calloutLeft, width: CALLOUT_W }}
        >
          <div className="flex items-baseline gap-2 mb-1.5">
            <span className="font-serif-it text-tb-navy text-xl leading-tight">
              {step.title}
            </span>
          </div>
          <p className="font-ui text-tb-navy/80 text-sm leading-relaxed">
            {step.body}
          </p>
          {/* Tab-icon progress row */}
          <div className="mt-3 flex items-center justify-center gap-1.5">
            {["explore", "dream", "chapters", "passport", "lounge"].map((t) => {
              const Icon = TAB_ICONS[t];
              const isCurrent = t === step.tab;
              const isPast = ["explore", "dream", "chapters", "passport", "lounge"].indexOf(t)
                            < ["explore", "dream", "chapters", "passport", "lounge"].indexOf(step.tab);
              return (
                <span
                  key={t}
                  className={
                    isCurrent ? "text-tb-coral"
                    : isPast    ? "text-tb-gold"
                    :             "text-tb-navy/25"
                  }
                >
                  <Icon className="w-3.5 h-3.5" />
                </span>
              );
            })}
          </div>
        </div>
      )}

      {status === "waiting" && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="font-hand text-white/80 italic">loading…</div>
        </div>
      )}
    </div>
  );
}
